import { motion } from 'framer-motion';
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CheckCircle2,
  Euro,
  FileText,
  Flame,
  Handshake,
  Home,
  Leaf,
  ShieldCheck,
  Thermometer,
  Tractor,
  TrendingDown,
  Users,
  Zap,
} from 'lucide-react';
import { useAudienceContent } from '../../hooks/useAudienceContent';
import { Container } from '../../components/layout/Container';
import { Reveal } from '../../components/motion/Reveal';
import { StaggerGrid, StaggerItem } from '../../components/motion/StaggerGrid';
import { CountUp } from '../../components/motion/CountUp';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { AidsCalculatorFunnel } from '../../components/tools/AidsCalculatorFunnel';
import { ServiceLd } from '../../components/seo/jsonld/Service';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';
import { FAQPageLd } from '../../components/seo/jsonld/FAQPage';
import { easeStripe, variantsStaggerContainer, variantsStaggerItem } from '../../lib/motion';

/* ─── Aid programs data ──────────────────────────────────── */
const AIDS = [
  {
    id: 'mpr-decarb',
    badge: 'MaPrimeRénov’',
    title: "MaPrimeRénov' Décarbonation",
    description:
      'Pour 1 à 2 gestes de travaux (isolation ou chauffage). Taux 30 % à 70 % selon revenus.',
    amountMin: '2 000',
    amountMax: '11 000',
    unit: '€',
    cumulable: ['Prime énergie (CEE)', 'Éco-PTZ', 'TVA 5,5 %'],
    icon: Thermometer,
    color: 'from-brand-500 to-brand-600',
    bgLight: 'bg-brand-50',
    textAccent: 'text-brand-700',
    borderAccent: 'border-brand-200',
    link: 'https://france-renov.gouv.fr/aides/mpr',
  },
  {
    id: 'mpr-ampleur',
    badge: 'MaPrimeRénov’',
    title: "MaPrimeRénov' Rénovation d’ampleur",
    description:
      'Rénovation globale sur 3 postes ou plus avec saut de classe DPE. Financement jusqu’à 70 % du coût total.',
    amountMin: '5 000',
    amountMax: '63 000',
    unit: '€',
    cumulable: ['CEE', 'Éco-PTZ', 'TVA 5,5 %'],
    icon: Home,
    color: 'from-brand-600 to-brand-800',
    bgLight: 'bg-brand-50',
    textAccent: 'text-brand-800',
    borderAccent: 'border-brand-300',
    link: 'https://france-renov.gouv.fr/aides/mpr-ampleur',
    highlight: true,
  },
  {
    id: 'mpr-copro',
    badge: 'MaPrimeRénov’',
    title: "MaPrimeRénov' Copropriétés",
    description:
      'Aide au syndicat pour travaux d’isolation ou de chauffage collectif. Dossier monté par le mandataire.',
    amountMin: '15 000',
    amountMax: '35 000',
    unit: '€',
    cumulable: ['CEE', 'TVA 5,5 %'],
    icon: Building2,
    color: 'from-lavender-500 to-lavender-700',
    bgLight: 'bg-lavender-50',
    textAccent: 'text-lavender-700',
    borderAccent: 'border-lavender-200',
    link: 'https://france-renov.gouv.fr/aides/mpr-copro',
  },
  {
    id: 'cee',
    badge: 'CEE',
    title: 'Primes énergie (CEE)',
    description:
      'Certificats d’Économies d’Énergie obligés par TotalEnergies, EDF, Engie, Leclerc… Aucune avance de trésorerie.',
    amountMin: '500',
    amountMax: '12 000',
    unit: '€',
    cumulable: ["MaPrimeRénov'", 'Éco-PTZ', 'TVA 5,5 %'],
    icon: Zap,
    color: 'from-accent-500 to-accent-600',
    bgLight: 'bg-accent-50',
    textAccent: 'text-accent-700',
    borderAccent: 'border-accent-200',
    link: '/cee',
  },
  {
    id: 'eco-ptz',
    badge: 'Éco-PTZ',
    title: 'Éco-prêt à taux zéro',
    description:
      'Prêt sans intérêts jusqu’à 50 000 € remboursable sur 20 ans. Aucune condition de ressources.',
    amountMin: '7 000',
    amountMax: '50 000',
    unit: '€',
    cumulable: ["MaPrimeRénov'", 'CEE'],
    icon: Euro,
    color: 'from-stone-600 to-stone-800',
    bgLight: 'bg-stone-50',
    textAccent: 'text-stone-700',
    borderAccent: 'border-stone-200',
    link: 'https://www.economie.gouv.fr/particuliers/eco-pret-taux-zero-eco-ptz',
  },
  {
    id: 'tva',
    badge: 'Fiscalité',
    title: 'TVA à 5,5 %',
    description:
      'TVA réduite à 5,5 % (au lieu de 10 %) sur les travaux de rénovation énergétique réalisés par un artisan RGE.',
    amountMin: '4',
    amountMax: '5',
    unit: ' % d’économie',
    cumulable: ["MaPrimeRénov'", 'CEE', 'Éco-PTZ'],
    icon: FileText,
    color: 'from-stone-500 to-stone-700',
    bgLight: 'bg-stone-50',
    textAccent: 'text-stone-600',
    borderAccent: 'border-stone-200',
    link: 'https://www.impots.gouv.fr/tva-taux-reduit-55',
  },
];

/* ─── Steps data ─────────────────────────────────────────── */
const STEPS = [
  {
    num: '01',
    title: 'Diagnostic',
    description:
      'Évaluation des travaux réalisables, calcul des aides disponibles et estimation du reste à charge. En 60 secondes.',
  },
  {
    num: '02',
    title: 'Audit énergétique',
    description:
      'Audit DPE ou audit réglementaire si rénovation d’ampleur (obligatoire pour MaPrimeRénov’ Ampleur).',
  },
  {
    num: '03',
    title: "Devis artisan RGE (Reconnu Garant de l'Environnement)",
    description:
      "Nous sélectionnons un artisan RGE (Reconnu Garant de l'Environnement) partenaire adapté à votre projet. Devis certifié conforme, aides intégrées en déduction directe.",
  },
  {
    num: '04',
    title: 'Montage du dossier',
    description:
      'Constitution et dépôt des dossiers MaPrimeRénov’ (ANAH) et CEE par notre équipe. Aucune démarche administrative pour vous.',
  },
  {
    num: '05',
    title: 'Travaux & versement de la prime',
    description:
      'Travaux réalisés par l’artisan RGE. MaPrimeRénov’ est versée par l’ANAH (4 à 8 semaines) ; la prime CEE par l’obligé partenaire (~30 jours), ou déduite directement de votre devis si elle a été intégrée dès le départ.',
  },
];

/* ─── Eligibility profiles ───────────────────────────────── */
const PROFILES = [
  {
    icon: Users,
    label: 'Propriétaires occupants',
    desc: 'Toutes tranches de revenus. Taux MPR 30 % à 70 % selon barème Anah 2026.',
    aides: ['MPR', 'CEE', 'Éco-PTZ', 'TVA 5,5 %'],
  },
  {
    icon: Handshake,
    label: 'Propriétaires bailleurs',
    desc: 'Sous condition d’engagement de location 6 ans. Taux MPR 20 % à 60 %.',
    aides: ['MPR', 'CEE', 'TVA 5,5 %'],
  },
  {
    icon: Building2,
    label: 'Copropriétés',
    desc: "MaPrimeRénov' Copropriétés pour travaux collectifs. Dossier monté par le syndic + mandataire.",
    aides: ["MaPrimeRénov' Copro", 'CEE', 'TVA 5,5 %'],
  },
  {
    icon: Home,
    label: 'Locataires',
    desc: 'Les primes énergie (CEE) peuvent bénéficier au locataire. Accord propriétaire requis pour travaux.',
    aides: ['CEE (partiel)'],
  },
];

/* ─── KPI data ───────────────────────────────────────────── */
const KPIS = [
  {
    value: 18500,
    suffix: ' €',
    label: 'aides cumulées en moyenne par dossier rénovation d’ampleur',
    source: 'Reporting interne 2025',
  },
  {
    value: 312,
    suffix: '',
    label: "dossiers MaPrimeRénov' + primes énergie (CEE) montés en 2025",
    source: 'Reporting interne 2025',
  },
  {
    value: 2,
    suffix: ' cls',
    label: 'de saut DPE moyen après rénovation d’ampleur',
    source: 'Observatoire SEF 2025',
  },
  {
    value: 0,
    suffix: ' €',
    label: 'avance de trésorerie pour l’artisan RGE',
    source: 'CGV mandataire primes énergie (CEE)',
  },
];

/* ─── FAQ data ───────────────────────────────────────────── */
const FAQ_ITEMS = [
  {
    question: 'MaPrimeRénov’ est-elle cumulable avec les primes CEE ?',
    answer:
      'Oui, MPR et CEE sont cumulables pour la plupart des travaux de rénovation énergétique. C’est même l’intérêt d’un dossier bien monté : maximiser les deux dispositifs simultanément. Solution Éco France, en tant que mandataire CEE et spécialiste MPR, gère les deux dépôts en parallèle.',
  },
  {
    question: 'Qu’est-ce que la rénovation d’ampleur et pourquoi est-elle avantageuse ?',
    answer:
      'La rénovation d’ampleur concerne les projets portant sur au moins 3 postes de travaux avec un saut de 2 classes DPE minimum. Elle ouvre droit à un plafond d’aide beaucoup plus élevé (jusqu’à 63 000 €) et nécessite un audit énergétique obligatoire.',
  },
  {
    question: 'L’artisan doit-il être certifié RGE ?',
    answer:
      'Oui, pour bénéficier de MaPrimeRénov’ et des primes CEE, les travaux doivent être réalisés par un artisan titulaire d’une certification RGE adaptée (QualiPAC, QualiPV, RGE Éco-Artisan…). Notre réseau de 120+ artisans partenaires est entièrement RGE.',
  },
  {
    question: 'Combien de temps faut-il pour recevoir les aides ?',
    answer:
      'Pour MaPrimeRénov’, le versement intervient après la fin des travaux et la validation du dossier ANAH — comptez 4 à 8 semaines. Pour les primes CEE, notre délai moyen de versement est d’environ 30 jours après dépôt du dossier.',
  },
  {
    question: 'Faut-il avancer les frais ?',
    answer:
      'Pour MPR : les aides sont versées après les travaux, sauf à utiliser l’avance MPR (accordée aux ménages très modestes, couvrant 70 % de l’aide). Pour les CEE : l’artisan partenaire peut déduire la prime directement du devis (zéro avance). L’éco-PTZ finance le reste à charge.',
  },
  {
    question: 'Votre accompagnement est-il gratuit ?',
    answer:
      'Notre accompagnement pour le montage du dossier MaPrimeRénov’ est inclus dans notre mission d’assistance à maîtrise d’ouvrage. Notre fonctionnement est détaillé dans notre charte de transparence économique. Aucun frais caché.',
  },
  {
    question: 'Puis-je faire les travaux moi-même ?',
    answer:
      'Non. MaPrimeRénov’ et les CEE exigent que les travaux soient réalisés par un artisan professionnel certifié RGE. Seule la TVA à 5,5 % est applicable aux achats de matériaux, sous conditions.',
  },
  {
    question: 'Y a-t-il des aides locales en plus ?',
    answer:
      'Oui : de nombreuses régions, départements et communes proposent des aides complémentaires (Île-de-France avec IDF Habitat, PACA, Auvergne-Rhône-Alpes…). L’ANAH propose également des aides spécifiques via le programme « Mon Accompagnateur Rénov’ ». Nous les intégrons dans le plan d’aides global.',
  },
];

/* ─── Testimonials ───────────────────────────────────────── */
const TESTIMONIALS = [
  {
    quote:
      "En 4 mois, on a isolé les combles, posé une PAC et changé les fenêtres. Le dossier a été bouclé par l'équipe. Prime totale : 14 200 €.",
    author: 'Marie-Hélène B.',
    role: 'Propriétaire — Maison individuelle 120 m², Lyon',
  },
  {
    quote:
      'Rénovation d’ampleur de notre immeuble : 3 sauts de classe DPE, 28 000 € d’aides MPR + 6 500 € de CEE. La coordinatrice a géré tout le dossier ANAH.',
    author: 'Syndicat Copropriété Les Acacias',
    role: 'Copropriété 12 lots — Grenoble',
  },
  {
    quote:
      'J’avais lu que les démarches MPR étaient complexes. Avec Solution Éco France, j’ai eu mon plan d’aides en 48h et la prime versée 5 semaines après la fin des travaux.',
    author: 'Pascal L.',
    role: 'Propriétaire bailleur — Appartement F3, Toulouse',
  },
];

/* ─── Page ───────────────────────────────────────────────── */
export default function RenovationPage() {
  const agriContent = useAudienceContent({
    agriculteurs: {
      badge: "Aides agricoles bâtiments d'exploitation 2026",
      h1line1: 'Aides agricoles pour vos',
      h1highlight: "bâtiments d'exploitation.",
      intro:
        "PCAE de votre Région, primes CEE agricoles, aides FranceAgriMer : on étudie l'éligibilité et on monte le dossier pour votre exploitation. (MaPrimeRénov' concerne uniquement les logements résidentiels, pas le bâti agricole.)",
      cta: 'Diagnostiquer mon exploitation',
      ctaHref: '/agriculteurs/diagnostic-cee',
    },
    default: null,
  });

  return (
    <>
      {/* SEO */}
      <ServiceLd
        name="Aides à la rénovation énergétique"
        description="MaPrimeRénov’, prime énergie (CEE), éco-PTZ, aides locales : on étudie l’éligibilité, on cumule les aides accessibles et on monte le dossier avec notre réseau d’artisans RGE partenaires."
        url="https://solutionecofrance.fr/renovation"
      />
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'Aides à la rénovation', url: 'https://solutionecofrance.fr/renovation' },
        ]}
      />
      <FAQPageLd items={FAQ_ITEMS} />

      {/* ── 1. Hero ──────────────────────────────────────── */}
      <section className="relative overflow-hidden border-b border-stone-200/80 bg-gradient-to-br from-stone-50 via-white to-brand-50/30 pb-20 pt-16 md:pb-28 md:pt-20">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-mesh-warm opacity-60" />
          <div className="absolute inset-0 bg-grid bg-[length:40px_40px] opacity-[0.025]" />
          <div className="absolute -right-32 -top-32 h-[560px] w-[560px] rounded-full bg-brand-200/20 blur-[120px]" />
        </div>

        <Container className="relative">
          <div className="grid items-start gap-12 lg:grid-cols-[1fr,480px]">
            {/* Left */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: easeStripe }}
              >
                <Badge variant={agriContent ? 'accent' : 'accent'} className="mb-4">
                  {agriContent?.badge ?? 'Rénovation énergétique 2026'}
                </Badge>
                {agriContent && (
                  <div className="mb-4 flex items-center gap-2 rounded-xl border border-accent-200 bg-accent-50/60 px-4 py-2.5 text-sm text-accent-800">
                    <Tractor className="h-4 w-4 shrink-0 text-accent-600" />
                    <span>
                      Contenu adapté aux exploitations agricoles — primes énergie (CEE) agricoles +
                      PCAE (aide régionale) + FranceAgriMer
                    </span>
                  </div>
                )}
                <h1 className="max-w-2xl text-balance font-display text-h-hero-lg font-bold tracking-tightish text-ink">
                  {agriContent ? agriContent.h1line1 : 'Toutes vos aides à la rénovation.'}{' '}
                  <span className="bg-gradient-to-r from-brand-600 to-accent-600 bg-clip-text text-transparent">
                    En un seul calcul.
                  </span>
                </h1>
                <p className="mt-5 max-w-xl text-lg leading-relaxed text-stone-600">
                  {agriContent?.intro ??
                    "MaPrimeRénov', prime énergie (CEE), éco-PTZ, TVA 5,5 %, aides locales : on regarde avec vous ce qui s'applique à votre logement, on cumule les aides accessibles et on monte le dossier avec un artisan RGE de notre réseau partenaire."}
                </p>

                <div className="mt-8 flex flex-wrap gap-2.5">
                  {[
                    {
                      icon: CheckCircle2,
                      label: "Artisans RGE (Reconnu Garant de l'Environnement) certifiés",
                    },
                    { icon: ShieldCheck, label: 'Mandataire primes énergie (CEE) agréé' },
                    {
                      icon: BadgeCheck,
                      label: 'Dossier MaPrimeRénov’ (ANAH) porté par notre équipe',
                    },
                    { icon: TrendingDown, label: 'Aucune avance artisan' },
                  ].map((pill) => {
                    const Icon = pill.icon;
                    return (
                      <span
                        key={pill.label}
                        className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white/80 px-3 py-1.5 text-[12px] font-medium text-stone-700 shadow-soft"
                      >
                        <Icon className="h-3.5 w-3.5 text-brand-600" strokeWidth={2} />
                        {pill.label}
                      </span>
                    );
                  })}
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Button asChild variant="brand" size="xl" className="gap-2">
                    <a href="/calculer-mes-aides">
                      Calculer mes aides
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </Button>
                  <Button asChild variant="secondary" size="lg">
                    <a href="/contact">Parler à un expert</a>
                  </Button>
                </div>

                <p className="mt-4 text-[12px] text-stone-400">
                  Gratuit · Sans engagement · Réponse sous 24h
                </p>
              </motion.div>
            </div>

            {/* Right — Calculator */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.55, delay: 0.1, ease: easeStripe }}
            >
              <div className="sticky top-24">
                <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500">
                  Calculateur d’aides cumulées — Estimez en 60 sec
                </p>
                <AidsCalculatorFunnel />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* ── 2. KPIs ──────────────────────────────────────── */}
      <section className="border-b border-stone-200/60 bg-white py-16">
        <Container>
          <motion.div
            variants={variantsStaggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            className="grid gap-px overflow-hidden rounded-2xl bg-stone-100 sm:grid-cols-2 lg:grid-cols-4"
          >
            {KPIS.map((kpi) => (
              <motion.div
                key={kpi.label}
                variants={variantsStaggerItem}
                className="bg-white p-8 transition-colors hover:bg-stone-50/60"
              >
                <p className="font-display text-4xl font-bold tracking-tight text-ink md:text-5xl">
                  <CountUp end={kpi.value} suffix={kpi.suffix} />
                </p>
                <p className="mt-2.5 text-sm leading-snug text-stone-600">{kpi.label}</p>
                <p className="mt-1.5 text-[11px] text-stone-400">Source : {kpi.source}</p>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </section>

      {/* ── 3. Aid programs ──────────────────────────────── */}
      <section id="aides" className="bg-stone-50/60 py-20 md:py-28">
        <Container>
          <Reveal>
            <div className="mb-12 max-w-2xl">
              <p className="eyebrow">Programmes d’aides 2026</p>
              <h2 className="mt-3 text-balance font-display text-display-3 font-bold tracking-tight text-ink">
                Six dispositifs cumulables.
              </h2>
              <p className="mt-4 text-body-lg leading-relaxed text-stone-600">
                Chaque programme a ses règles, ses plafonds, ses conditions d’éligibilité. Nous les
                combinons pour maximiser votre enveloppe totale.
              </p>
            </div>
          </Reveal>

          <StaggerGrid className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {AIDS.map((aid) => {
              const Icon = aid.icon;
              return (
                <StaggerItem key={aid.id}>
                  <article
                    className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-white p-6 shadow-soft transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-elevated ${aid.borderAccent} ${aid.highlight ? 'ring-2 ring-brand-400/40 ring-offset-1' : ''}`}
                  >
                    {aid.highlight && (
                      <span className="absolute right-4 top-4 rounded-full bg-brand-600 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        Recommandé
                      </span>
                    )}

                    <div className="flex items-start gap-3">
                      <div
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white ${aid.color}`}
                      >
                        <Icon className="h-5 w-5" strokeWidth={1.8} />
                      </div>
                      <div>
                        <span
                          className={`text-[10px] font-bold uppercase tracking-widest ${aid.textAccent}`}
                        >
                          {aid.badge}
                        </span>
                        <h3 className="mt-0.5 font-display text-h-pillar text-ink">{aid.title}</h3>
                      </div>
                    </div>

                    <p className="mt-4 flex-1 text-[13px] leading-relaxed text-stone-600">
                      {aid.description}
                    </p>

                    <div className={`mt-5 rounded-xl ${aid.bgLight} p-3`}>
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">
                        Montant estimé
                      </p>
                      <p className={`mt-1 text-xl font-bold ${aid.textAccent}`}>
                        {aid.amountMin} – {aid.amountMax} {aid.unit}
                      </p>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-1.5">
                      <span className="text-[10px] text-stone-400">Cumulable :</span>
                      {aid.cumulable.map((c) => (
                        <span
                          key={c}
                          className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-600"
                        >
                          {c}
                        </span>
                      ))}
                    </div>

                    <a
                      href={aid.link}
                      target={aid.link.startsWith('http') ? '_blank' : undefined}
                      rel={aid.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className={`mt-4 inline-flex items-center gap-1 text-[12px] font-semibold ${aid.textAccent} underline-offset-2 hover:underline`}
                    >
                      En savoir plus
                      <ArrowRight className="h-3.5 w-3.5" />
                    </a>
                  </article>
                </StaggerItem>
              );
            })}
          </StaggerGrid>
        </Container>
      </section>

      {/* ── 4. Steps ─────────────────────────────────────── */}
      <section className="bg-white py-20 md:py-28">
        <Container>
          <Reveal>
            <div className="mb-14 max-w-2xl">
              <p className="eyebrow">Notre accompagnement</p>
              <h2 className="mt-3 text-balance font-display text-display-3 font-bold tracking-tight text-ink">
                Cinq étapes, zéro flou.
              </h2>
              <p className="mt-4 text-body-lg leading-relaxed text-stone-600">
                De l’estimation au versement des aides, nous pilotons le parcours administratif et
                technique avec notre réseau d’artisans RGE partenaires. Les primes sont versées par
                les organismes officiels (ANAH pour MaPrimeRénov’, obligé partenaire pour la prime
                énergie — CEE), intégrées au devis ou versées directement selon le dispositif.
              </p>
            </div>
          </Reveal>

          <div className="relative">
            <div
              aria-hidden
              className="absolute left-0 right-0 top-[22px] hidden h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent lg:block"
            />
            <StaggerGrid>
              <div className="grid gap-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-5">
                {STEPS.map((step) => (
                  <StaggerItem key={step.num} className="relative">
                    <div className="flex items-center gap-3">
                      <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-stone-200 bg-white font-mono text-[13px] font-bold text-ink shadow-soft">
                        {step.num}
                      </span>
                    </div>
                    <h3 className="mt-4 font-display text-[15px] font-semibold tracking-tight text-ink">
                      {step.title}
                    </h3>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-stone-500">
                      {step.description}
                    </p>
                  </StaggerItem>
                ))}
              </div>
            </StaggerGrid>
          </div>

          <Reveal delay={0.2}>
            <div className="mt-14 flex flex-wrap gap-3">
              <Button asChild variant="brand" size="lg" className="gap-2">
                <a href="/calculer-mes-aides">
                  Calculer mes aides
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <Button asChild variant="secondary" size="lg">
                <a href="/cas-clients">Voir des cas concrets →</a>
              </Button>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* ── 5. Eligibility ───────────────────────────────── */}
      <section className="bg-stone-50/80 py-20 md:py-24">
        <Container>
          <Reveal>
            <div className="mb-12 max-w-2xl">
              <p className="eyebrow">Qui est éligible ?</p>
              <h2 className="mt-3 text-balance font-display text-display-3 font-bold tracking-tight text-ink">
                Quatre profils, des droits différents.
              </h2>
            </div>
          </Reveal>

          <StaggerGrid className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PROFILES.map((profile) => {
              const Icon = profile.icon;
              return (
                <StaggerItem key={profile.label}>
                  <div className="flex h-full flex-col rounded-2xl border border-stone-200 bg-white p-6 shadow-soft">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                      <Icon className="h-5 w-5" strokeWidth={1.7} />
                    </div>
                    <h3 className="mt-4 text-[15px] font-semibold text-ink">{profile.label}</h3>
                    <p className="mt-2 flex-1 text-[13px] leading-relaxed text-stone-500">
                      {profile.desc}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {profile.aides.map((a) => (
                        <span
                          key={a}
                          className="rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-semibold text-brand-700"
                        >
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGrid>

          {/* Works eligible */}
          <Reveal delay={0.1}>
            <div className="mt-10 rounded-2xl border border-stone-200 bg-white p-6 shadow-soft md:p-8">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-50">
                  <Leaf className="h-5 w-5 text-accent-700" strokeWidth={1.7} />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-stone-400">
                    Travaux éligibles
                  </p>
                  <h3 className="text-[15px] font-semibold text-ink">
                    Quels travaux donnent droit aux aides ?
                  </h3>
                </div>
              </div>
              <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  { icon: Thermometer, label: 'Isolation toiture / combles' },
                  { icon: Home, label: 'Isolation murs ext./int.' },
                  { icon: Zap, label: 'Pompe à chaleur' },
                  { icon: Flame, label: 'Chaudière biomasse/bois' },
                  { icon: Euro, label: 'Fenêtres double/triple vitrage' },
                  { icon: TrendingDown, label: 'Ventilation double flux (VMC)' },
                  { icon: Leaf, label: 'Chauffe-eau thermodynamique' },
                  { icon: FileText, label: 'Audit énergétique réglementaire' },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.label}
                      className="flex items-center gap-2.5 rounded-xl bg-stone-50 px-3 py-2.5"
                    >
                      <Icon className="h-4 w-4 shrink-0 text-brand-600" strokeWidth={1.7} />
                      <span className="text-[12px] font-medium text-stone-700">{item.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* ── 6. Testimonials ──────────────────────────────── */}
      <section className="bg-white py-20 md:py-24">
        <Container>
          <Reveal>
            <div className="mb-10 max-w-2xl">
              <p className="eyebrow">Cas clients</p>
              <h2 className="mt-3 text-balance font-display text-display-3 font-bold tracking-tight text-ink">
                Ils ont maximisé leurs aides avec nous.
              </h2>
            </div>
          </Reveal>

          <StaggerGrid className="grid gap-5 lg:grid-cols-3">
            {TESTIMONIALS.map((t) => (
              <StaggerItem key={t.author}>
                <div className="flex h-full flex-col rounded-2xl border border-stone-200 bg-stone-50/50 p-6 shadow-soft">
                  <blockquote className="flex-1 text-[14px] leading-relaxed text-stone-700">
                    « {t.quote} »
                  </blockquote>
                  <div className="mt-5 border-t border-stone-100 pt-4">
                    <p className="text-[13px] font-semibold text-ink">{t.author}</p>
                    <p className="mt-0.5 text-[12px] text-stone-500">{t.role}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGrid>

          <Reveal delay={0.1}>
            <div className="mt-8 text-center">
              <Button asChild variant="secondary" size="lg" className="gap-2">
                <a href="/cas-clients">
                  Voir tous les cas clients
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* ── 7. FAQ ───────────────────────────────────────── */}
      <section id="faq" className="bg-stone-50 py-20 md:py-24">
        <Container size="narrow">
          <Reveal>
            <div className="mb-10">
              <p className="eyebrow">FAQ</p>
              <h2 className="mt-3 text-balance font-display text-display-3 font-bold tracking-tight text-ink">
                Questions fréquentes sur les aides à la rénovation.
              </h2>
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <Accordion
              type="single"
              collapsible
              className="rounded-2xl border border-stone-200 bg-white px-6 shadow-soft"
            >
              {FAQ_ITEMS.map((item, idx) => (
                <AccordionItem key={item.question} value={`q-${idx}`}>
                  <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                  <AccordionContent className="text-stone-600">{item.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </Container>
      </section>

      {/* ── 8. CTA Final ─────────────────────────────────── */}
      <section className="relative overflow-hidden bg-ink py-24 md:py-32">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-mesh-dark opacity-100" />
          <div className="absolute -top-1/2 left-1/4 h-[600px] w-[600px] rounded-full bg-accent-500/20 blur-[120px]" />
          <div className="absolute -bottom-1/2 right-1/4 h-[600px] w-[600px] rounded-full bg-brand-500/30 blur-[120px]" />
          <div className="absolute inset-0 bg-noise opacity-[0.06] mix-blend-overlay" />
        </div>

        <Container className="relative">
          <Reveal>
            <div className="mx-auto max-w-3xl text-center">
              <h2
                className="text-balance text-4xl font-bold tracking-tightish md:text-5xl"
                style={{
                  backgroundImage: 'linear-gradient(120deg, #ffffff 0%, #d1fae5 45%, #84cc16 100%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Maximisez votre plan d’aides.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-stone-300 md:text-xl">
                Acteur intégré : ingénierie financière + réseau d’artisans RGE partenaires. Nous
                cadrons, documentons et déposons chaque ligne du dossier d’aides. Sans avance pour
                l’artisan, sans avance pour vous quand les primes sont intégrées au devis.
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                <Button asChild size="xl" variant="accent" className="group gap-2">
                  <a href="/calculer-mes-aides">
                    Calculer mes aides
                    <ArrowRight className="h-4 w-4 transition-transform duration-350 ease-stripe group-hover/btn:translate-x-1" />
                  </a>
                </Button>
                <Button
                  asChild
                  size="xl"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white hover:text-ink"
                >
                  <a href="/contact">Parler à un conseiller</a>
                </Button>
              </div>
              <p className="mt-7 text-xs uppercase tracking-[0.18em] text-stone-500">
                Gratuit · Sans engagement · Données France Rénov’
              </p>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
